import React from 'react'
import styled from 'styled-components'
import { rgba } from 'polished'
import ScrollAnimation from 'react-animate-on-scroll'

import { media, mediaDown } from 'styles/media'
import { white } from 'styles/colors'
import { fontWeights, H1, H2 } from 'styles/typography'
import { cover, navHeightMobile, navHeight } from 'styles/global'
import * as spacing from 'styles/spacing'

import Button from 'components/button/Button'
import Header from 'components/Header'

const Container = styled.div`
  position: relative;
  background-color: #2a353b;
  background-size: cover;
  background-position: center;
  height: 580px;
  background-image: url('${process.env.BASE_PATH}/images/epod2/hero/bg.jpg');

  ${media.medium`
    height: 620px;
  `};
`

const HeaderWrap = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: ${navHeightMobile};

  ${media.medium`
    height: ${navHeight};
  `};

  ${mediaDown.medium`
    border-bottom: 1px solid ${rgba(white, 0.5)};
  `};
`

const Inner = styled.div`
  position: relative;
  height: 100%;
`

const DeviceWrap = styled.div`
  position: absolute;
  top: 57%;
  left: 50%;
  transform: translate3d(-50%, -50%, 0);
  width: 150px;

  ${media.medium`
    width: 190px;
    top: 50%;
  `};
`

const DeviceInner = styled.div`
  position: relative;
  width: 100%;

  &:after {
    content: '';
    display: block;
    width: 100%;
    padding-bottom: 272%;
  }
`

const Device = styled.img`
  ${cover('absolute')};
`

const Content = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate3d(-50%, -50%, 0);
  z-index: 10;

  ${mediaDown.medium`
    top: 67%;
    text-align: center;
    width: 100%;
    padding: 0 ${spacing.large};
  `};

  ${media.medium`
    top: 50%;
    margin-left: -92px;
    margin-top: 22px;
    width: 620px;
  `};

  ${media.large`
    margin-left: -119px;
    margin-top: 19px;
    width: 820px;
  `};
`

const Eyebrow = styled(H2)`
  font-weight: ${fontWeights.regular};
  color: ${white};
  margin-bottom: 0;
  font-size: 15px;

  ${mediaDown.medium`
    margin-bottom: ${spacing.large} !important;
  `};

  ${media.small`
    font-size: 16px;
  `};

  ${media.medium`
    font-size: 23px;
    color: ${rgba(white, 0.66)};
  `};

  ${media.large`
    font-size: 30.6px;
  `};
`

const Heading = styled(H1)`
  font-family: 'Trim Poster';
  color: ${white};
  margin-bottom: 0.2em !important;

  ${mediaDown.medium`
    display: flex;
    align-items: center;
    justify-content: center;
    margin-left: -50px;
  `};

  span {
    position: static;

    ${mediaDown.medium`
      text-align: right;
    `};
  }

  em {
    position: relative;
    color: transparent;
    font-style: normal;
    -webkit-text-stroke: 1px ${white};
    margin-left: ${spacing.xLarge};

    ${media.medium`
      -webkit-text-stroke: 2px ${white};
      margin-left: 0.6em;
    `};

    ${media.large`
      margin-left: 0.4em;
    `};

    &:after,
    &:before {
      position: absolute;
      top: 4%;
      left: 0;
      right: 0;
      content: attr(data-text);
      filter: blur(2px);
      white-space: nowrap;

      ${media.medium`
        filter: blur(4px);
      `};
    }
  }

  ${media.medium`
    font-size: 60px;
    white-space: nowrap; /* Force onto 1 line */
    margin-bottom: 0.1em !important;
  `};

  ${media.large`
    font-size: 80px;
  `};
`

const Buttons = styled.div`
  display: flex;
  justify-content: center;

  ${media.medium`
    justify-content: flex-start;
  `};
`

const StyledButton = styled(Button)`
  position: relative;
  border-width: 1px;
  padding: 0;
  min-width: 150px;

  & + & {
    margin-left: ${spacing.medium};

    ${media.medium`
      margin-left: ${spacing.large};
    `};
  }

  ${media.xSmall`
    min-width: 156px;
  `};

  ${media.small`
    min-width: 175px;
  `};

  ${media.medium`
    min-width: 190px;
  `};

  ${media.large`
    min-width: 220px;
  `};

  span {
    text-transform: uppercase;
    font-weight: ${fontWeights.regular};
    letter-spacing: 0.1em;
  }
`

const Hero = () => (
  <Container>
    <HeaderWrap>
      <Header transparent />
    </HeaderWrap>
    <Inner>
      <Content>
        <ScrollAnimation animateIn="fadeInUp" offset={0} delay={200} animateOnce>
          <Eyebrow className="u-hide-medium-down">REDESIGNED TO POWER YOUR ePod EXPERIENCE</Eyebrow>
        </ScrollAnimation>
        <ScrollAnimation animateIn="fadeInUp" offset={0} delay={400} animateOnce>
          <Heading>
            <span>
              THE
              <br className="u-hide-medium-up" /> NEW VUSE
            </span>
            <em data-text="ePod 2">ePod 2</em>
          </Heading>
        </ScrollAnimation>
        <ScrollAnimation animateIn="fadeInUp" offset={0} delay={600} animateOnce>
          <Eyebrow className="u-hide-medium-up">
            REDESIGNED TO POWER YOUR <br /> ePod EXPERIENCE
          </Eyebrow>
        </ScrollAnimation>
        <Buttons as={ScrollAnimation} animateIn="fadeInUp" offset={0} delay={800} animateOnce>
          <StyledButton
            href="https://ca-experience-epod2.vuse.com/"
            theme="custom"
            customColor={white}
            variant="outline"
            target="_blank"
          >
            Learn more
          </StyledButton>
          <StyledButton to="/buy-online/epod2/" theme="custom" customColor={white} variant="outline">
            Buy now - $9.99
          </StyledButton>
        </Buttons>
      </Content>
      <DeviceWrap>
        <ScrollAnimation animateIn="fadeIn" offset={0} delay={600} animateOnce>
          <DeviceInner>
            <Device src={`${process.env.BASE_PATH}/images/epod2/hero/device.png`} />
          </DeviceInner>
        </ScrollAnimation>
      </DeviceWrap>
    </Inner>
  </Container>
)

export default Hero
